import * as z from 'zod'

/**
 * The trim of a Proof claim recording (the "director's cut" of `codesmith_proof_directors_cut`): how the player shows the stored MP4, never a
 * second file. Post-processing builds it from the recording script after the claim closes and
 * stores it as `cut` on the `video` evidence item. Every time is on the recording's own clock,
 * so chapters, events, click rings and `&t=` links need no remapping.
 */

/** 150 speedups are at most 8.7 KB of compact JSON (57 bytes each) and 60 zooms 4.5 KB, inside a claim's evidence budget together. */
const PROOF_CUT_SPEEDUPS_MAX = 150
const PROOF_CUT_ZOOMS_MAX = 60

const MediaMs = z.number().int().nonnegative().max(2_147_483_647)
const FramePx = z.number().int().nonnegative().max(32_767)

/** `[start_ms, end_ms)` plays at 2× or not at all (`skip`: the player seeks to `end_ms`). Everything outside a speedup plays at 1×. */
type ProofRecordingSpeedup = z.infer<typeof ProofRecordingSpeedup>
const ProofRecordingSpeedup = z.object({
  start_ms: MediaMs,
  end_ms: MediaMs,
  rate: z.union([z.literal(2), z.literal('skip')]),
})

/**
 * While `[start_ms, end_ms)` plays, the camera frames the rectangle `(x, y, w, h)` of the encoded
 * frame, in its pixels. A zoom that starts where the previous ends is one camera move from the
 * first window to the second; the player's spring decides the path.
 */
export type ProofRecordingZoom = z.infer<typeof ProofRecordingZoom>
export const ProofRecordingZoom = z.object({
  start_ms: MediaMs,
  end_ms: MediaMs,
  x: FramePx,
  y: FramePx,
  w: FramePx.min(1),
  h: FramePx.min(1),
})

export type ProofRecordingCut = z.infer<typeof ProofRecordingCut>
export const ProofRecordingCut = z
  .object({
    speedups: z.array(ProofRecordingSpeedup).max(PROOF_CUT_SPEEDUPS_MAX),
    // A cut stored before zooms existed carries none.
    zooms: z.array(ProofRecordingZoom).max(PROOF_CUT_ZOOMS_MAX).default([]),
  })
  .superRefine((cut, ctx) => {
    for (const key of ['speedups', 'zooms'] as const) {
      cut[key].forEach((range, index) => {
        if (range.end_ms <= range.start_ms) {
          ctx.addIssue({
            code: 'custom',
            message: 'a range must have positive length',
            path: [key, index, 'end_ms'],
          })
        }
        const previous = cut[key][index - 1]
        if (previous && range.start_ms < previous.end_ms) {
          ctx.addIssue({
            code: 'custom',
            message: 'ranges must be ordered by start_ms and must not overlap',
            path: [key, index, 'start_ms'],
          })
        }
      })
    }
  })

/**
 * The speedup playing at `sourceMs`, if any. A moment on a speedup's end belongs to what follows
 * it. The player sets its rate from it and, inside a skip, seeks to its end.
 */
export function speedupAt(
  cut: ProofRecordingCut,
  sourceMs: number
): ProofRecordingSpeedup | undefined {
  return cut.speedups.find((range) => range.start_ms <= sourceMs && sourceMs < range.end_ms)
}

/** The zoom in force at `sourceMs`, if any; the player's camera springs toward its window, or cuts to it under reduced motion. */
export function zoomAt(cut: ProofRecordingCut, sourceMs: number): ProofRecordingZoom | undefined {
  return cut.zooms.find((range) => range.start_ms <= sourceMs && sourceMs < range.end_ms)
}

function removedMs({ start_ms, end_ms, rate }: ProofRecordingSpeedup): number {
  const shown = end_ms - start_ms
  return rate === 'skip' ? shown : shown / 2
}

/**
 * Playback length of the cut: `(76_000, [{8000–68_000 skip}])` → `16_000`; `(20_000, [{4000–8000 ×2}])` → `18_000`.
 * Here and below `cut` is the one in force, null while the full recording plays: the clock is then the recording's own.
 */
export function cutDurationMs(durationMs: number, cut: ProofRecordingCut | null): number {
  if (!cut) return durationMs
  return Math.round(cut.speedups.reduce((total, speedup) => total - removedMs(speedup), durationMs))
}

/**
 * Where a moment of the recording lands on the cut's clock: `[{4000–8000 ×2}, {10_000–20_000 skip}]`
 * → `3000` → `3000`, `6000` → `5000`, `15_000` → `8000` (a skipped moment reads as the skip's cut), `25_000` → `13_000`.
 */
export function cutMsAt(sourceMs: number, cut: ProofRecordingCut | null): number {
  if (!cut) return sourceMs
  let removed = 0
  for (const speedup of cut.speedups) {
    if (sourceMs < speedup.start_ms) break
    const inside = Math.min(sourceMs, speedup.end_ms) - speedup.start_ms
    removed += speedup.rate === 'skip' ? inside : inside / 2
  }
  return Math.round(sourceMs - removed)
}

/**
 * The inverse of `cutMsAt` for the clock the player scrubs: a cut time inside a 2× stretch maps
 * into it, and the cut of a skip maps to the skip's end, since no frame of a skip is ever shown.
 */
export function sourceMsAt(cutMs: number, cut: ProofRecordingCut | null): number {
  if (!cut) return cutMs
  let removed = 0
  for (const speedup of cut.speedups) {
    const cutStart = speedup.start_ms - removed
    if (cutMs < cutStart) break
    const shown = speedup.end_ms - speedup.start_ms
    if (speedup.rate === 'skip') {
      removed += shown
      continue
    }
    const cutEnd = cutStart + shown / 2
    if (cutMs < cutEnd) return Math.round(speedup.start_ms + (cutMs - cutStart) * 2)
    removed += shown / 2
  }
  return Math.round(cutMs + removed)
}
